import type {
	ReturnRequestDto,
	ReturnRequestLightDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Avatar, AvatarFallback } from "@babackapp/ui/components/avatar";
import { Badge } from "@babackapp/ui/components/badge";
import { Skeleton } from "@babackapp/ui/components/skeleton";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { CalendarIcon, TruckIcon } from "@heroicons/react/24/outline";
import {
	type ColumnDef,
	getCoreRowModel,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { formatDate } from "date-fns";
import { MessageCircle } from "lucide-react";
import { useMemo } from "react";
import ReturnStatusBadge from "@/pages/(app)/returns/components/return-status-badge";
import { useReturnRequestsFilters } from "../../providers/use-return-requests-filters";

type UseReturnsRequestTableParams = {
	returnRequests: ReturnRequestLightDto[];
	isLoading: boolean;
};

function useColumns() {
	const t = useTranslation();

	const columns: ColumnDef<ReturnRequestDto>[] = [
		{
			id: "order",
			accessorKey: "order",
			header: t("returns.header.order"),
			enableSorting: true,
			cell({ row }) {
				return (
					<div className="flex gap-2 align-middle">
						<div className="flex-col flex-1 flex">
							<span className="text-sm font-semibold">
								{row.original?.orderName}
							</span>
							<span className="relative flex-1">
								<span className="absolute text-xs text-muted-foreground group-hover:opacity-0 translate-x-0 group-hover:-translate-x-4 ease-in-out duration-200 transition-all">
									{row.original?.orderId}
								</span>
								<span className="absolute text-xs text-muted-foreground translate-x-4 group-hover:translate-x-0 transition-all ease-in-out duration-200 group-hover:opacity-100 opacity-0 whitespace-nowrap">
									{row.original?.line_items?.length} articles
								</span>
							</span>
						</div>
					</div>
				);
			},
		},
		{
			id: "customer",
			accessorKey: "customer",
			enableSorting: false,
			header: t("returns.header.customer"),
			cell({ row }) {
				const customer = row.original.customer;
				if (!customer) return null;
				const name = [customer.first_name, customer.last_name]
					.filter(Boolean)
					.join(" ");
				const email = customer.email;
				return (
					<div className="flex gap-4 items-center flex-1 overflow-hidden">
						<Avatar>
							<AvatarFallback>{name.charAt(0).toUpperCase()}</AvatarFallback>
						</Avatar>
						<div className="flex flex-col">
							<span>{name}</span>
							<span className="text-xs text-muted-foreground">{email}</span>
						</div>
					</div>
				);
			},
			minSize: 300,
		},
		{
			id: "tracking_code",
			accessorKey: "shipping_info.tracking_code",
			header: t("returns.header.tracking"),
			enableSorting: false,
			cell({ row }) {
				const code = row.original?.shipping_info?.tracking_code;

				if (!code) return null;

				return (
					<Badge variant="outline" className="max-w-[180px]">
						<TruckIcon className="w-4 h-4 mr-2 flex-shrink-0" />
						<span className="truncate">{code}</span>
					</Badge>
				);
			},
		},
		{
			id: "status",
			accessorKey: "status",
			enableSorting: false,
			header: t("returns.header.status"),
			cell({ row }) {
				return <ReturnStatusBadge statuses={row.original?.status} />;
			},
			minSize: 300,
		},
		{
			id: "createdAt",
			accessorKey: "createdAt",
			enableSorting: true,
			header: t("returns.header.createdAt"),
			cell({ row }) {
				if (!row.original?.createdAt) return null;
				return (
					<span className="text-xs font-semibold inline-flex items-center text-secondary-foreground">
						<CalendarIcon className="w-4 h-4 mr-1" />
						{formatDate(row.original.createdAt, "dd/MM/yyyy")}
					</span>
				);
			},
		},
		{
			id: "treatedLogisticianAt",
			accessorKey: "treatedLogisticianAt",
			enableSorting: true,
			header: t("returns.header.partiallyTreatedLogistician"),
			cell({ row }) {
				if (
					!row.original?.status?.partiallyTreatedLogistician?.doneAt ||
					row.original?.status?.partiallyTreatedLogistician?.doneAt === -1
				)
					return null;

				return (
					<span className="text-xs font-semibold inline-flex items-center text-secondary-foreground">
						<CalendarIcon className="w-4 h-4 mr-1" />
						{formatDate(
							row.original.status.partiallyTreatedLogistician.doneAt,
							"dd/MM/yyyy",
						)}
					</span>
				);
			},
		},
		{
			id: "treatedMerchantAt",
			accessorKey: "treatedMerchantAt",
			enableSorting: true,
			header: t("returns.header.partiallyTreatedMerchant"),
			cell({ row }) {
				if (
					!row.original?.status?.partiallyTreatedMerchant?.doneAt ||
					row.original?.status?.partiallyTreatedMerchant.doneAt === -1
				)
					return null;

				return (
					<span className="text-xs font-semibold inline-flex items-center text-secondary-foreground">
						<CalendarIcon className="w-4 h-4 mr-1" />
						{formatDate(
							row.original.status.partiallyTreatedMerchant.doneAt,
							"dd/MM/yyyy",
						)}
					</span>
				);
			},
		},
		{
			id: "shippedAt",
			accessorKey: "shippedAt",
			enableSorting: true,
			header: t("returns.header.shipped"),
			cell({ row }) {
				if (
					!row.original.status.shipped.doneAt ||
					row.original.status.shipped.doneAt === -1
				)
					return null;

				return (
					<span className="text-xs font-semibold inline-flex items-center text-secondary-foreground">
						<CalendarIcon className="w-4 h-4 mr-1" />
						{formatDate(row.original.status.shipped.doneAt, "dd/MM/yyyy")}
					</span>
				);
			},
		},
		{
			id: "comments",
			accessorKey: "comments",
			enableSorting: false,
			header: t("returnRequest.comments"),
			cell({ row }) {
				const length = row.original.logs.filter(
					(log) => log.type === "comment",
				).length;

				if (!length) return null;

				return (
					<div className="flex items-center">
						<MessageCircle className="w-4 h-4 mr-2" />
						<span className="text-xs text-muted-foreground">{length}</span>
					</div>
				);
			},
		},
		{
			id: "assignedTo",
			accessorKey: "assignedTo",
			enableSorting: false,
			header: t("returns.assigned"),
			cell({ row }) {
				const assignedTo = row.original.assignedUsers;

				if (!assignedTo) return null;

				return (
					<div className="flex -space-x-4 items-center">
						{assignedTo.map((user) => (
							<Tooltip>
								<TooltipTrigger>
									<Avatar>
										<AvatarFallback className="border-black/10 border">
											{user.charAt(0).toUpperCase()}
										</AvatarFallback>
									</Avatar>
								</TooltipTrigger>
								<TooltipContent>
									<span>{user}</span>
								</TooltipContent>
							</Tooltip>
						))}
					</div>
				);
			},
		},
	];

	return columns;
}

export const columnIds = [
	"order",
	"customer",
	"tracking_code",
	"status",
	"createdAt",
	"logisticianTreatedAt",
	"merchantTreatedAt",
	"shippedAt",
	"comments",
	"assignedTo",
] as const;

function useColumnVisibility() {
	const { view } = useReturnRequestsFilters();

	const visibility = useMemo(() => {
		return columnIds.reduce((acc, next) => {
			if (!view) return { ...acc, [next]: true };
			return {
				...acc,
				[next]:
					view.fields.find((field) => field.name === next)?.active || false,
			};
		}, {} as VisibilityState);
	}, [view]);

	return visibility;
}

export default function useReturnRequestsTable({
	returnRequests,
	isLoading,
}: UseReturnsRequestTableParams) {
	const { setSorting, sorting } = useReturnRequestsFilters();

	const tableData = useMemo(
		() => (isLoading ? Array(15).fill({}) : returnRequests.filter(Boolean)),
		[isLoading, returnRequests],
	);

	const columns = useColumns();

	const columnVisibility = useColumnVisibility();

	const tableColumns = useMemo(() => {
		return isLoading
			? columns.map((column) => {
					return {
						...column,
						cell: () => <Skeleton className="h-4 w-[100px]" />,
					};
				})
			: columns;
	}, [isLoading]);

	const table = useReactTable({
		data: tableData,
		columns: tableColumns,
		getCoreRowModel: getCoreRowModel(),
		manualPagination: true,
		manualFiltering: true,
		columnResizeMode: "onChange",
		columnResizeDirection: "ltr",
		defaultColumn: {
			minSize: 200,
		},
		state: {
			sorting,
			columnVisibility,
		},
		onSortingChange: (newSorting) => {
			const nextValue =
				typeof newSorting === "function" ? newSorting(sorting) : newSorting;
			setSorting(nextValue);
		},
	});

	return table;
}
